import { DoqType, DoqWithSideContent } from '@ardoq/doq';
import { ViewpointBuilderAdvancedSearchState } from 'viewpointBuilder/selectContextTab/types';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';

type EmptyStateProps = {
  hasResults: boolean;
  advancedSearchState: ViewpointBuilderAdvancedSearchState;
  i18nLocale: I18nLocale;
};

export const AdvancedSearchEmptyStates = ({
  hasResults,
  advancedSearchState,
  i18nLocale,
}: EmptyStateProps) => {
  return advancedSearchState ? (
    <FlexBox justify="center" align="center" flex={1}>
      {advancedSearchState.status === 'invalid' && (
        <InvalidQuery i18nLocale={i18nLocale} />
      )}
      {advancedSearchState.status === 'empty' && (
        <NoQuery i18nLocale={i18nLocale} />
      )}
      {advancedSearchState.status === 'valid' && !hasResults && (
        <NoResult i18nLocale={i18nLocale} />
      )}
    </FlexBox>
  ) : null;
};

const NoQuery = ({ i18nLocale }: WithI18nLocale) => {
  return (
    <DoqWithSideContent
      doqType={DoqType.WAITING}
      title={t(
        i18nLocale,
        'viewpointBuilder:selectContext.advancedSearch.noQuery.title',
        'Find components with advanced search'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.advancedSearch.noQuery.text',
          'Add rules to find specific components for your dataset. The rules can be based on different component properties like who created it or its field values.'
        )}
      </p>
    </DoqWithSideContent>
  );
};

const InvalidQuery = ({ i18nLocale }: WithI18nLocale) => {
  return (
    <DoqWithSideContent
      doqType={DoqType.MISSING_ORG}
      title={t(
        i18nLocale,
        'viewpointBuilder:selectContext.advancedSearch.invalidQuery.title',
        'There is an incomplete rule or subquery'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.advancedSearch.invalidQuery.text',
          "A rule or subquery you added isn't completely filled. Check and fill all the fields to search for components."
        )}
      </p>
    </DoqWithSideContent>
  );
};

const NoResult = ({ i18nLocale }: WithI18nLocale) => {
  return (
    <DoqWithSideContent
      doqType={DoqType.EMPTY_BOX}
      title={t(
        i18nLocale,
        'viewpointBuilder:selectContext.advancedSearch.noResults.title',
        'No results found'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.advancedSearch.noResults.text',
          'There is no component that matches the rules you added. Try a different search or adjust your rules.'
        )}
      </p>
    </DoqWithSideContent>
  );
};
